import React, { useState, useEffect, useCallback, useRef } from 'react'
import KPICards from './KPICards'
import StudentTable from './StudentTable'
import ExamTable from './ExamTable'
import StudentModal from './StudentModal'
import ExamSheetModal from './ExamSheetModal'
import Charts from './Charts'
import ExamCharts from './ExamCharts'
import { ensureFreshToken, fetchWithAuthRetry } from '../authFetch'

const API_BASE = '/api'
// Max extra departments loadable alongside the primary. Must match
// MAX_EXTRA_DEPTS in backend/routes/dashboard.py.
const MAX_EXTRA_DEPTS = 200

// Single-flight token refresh + self-healing fetch — shared by every
// component that touches Absorb-backed routes (see src/authFetch.js).

function Dashboard({ user, department, onLogout, initialData }) {
  const [students, setStudents] = useState(initialData?.students || [])
  const [summary, setSummary] = useState(initialData?.summary || null)
  const [loading, setLoading] = useState(!initialData)
  const [syncing, setSyncing] = useState(false)
  const [loadingFull, setLoadingFull] = useState(initialData?.isQuick || false)
  const [error, setError] = useState(null)
  const [lastSynced, setLastSynced] = useState(initialData ? new Date() : null)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [showCharts, setShowCharts] = useState(false)
  const [showExamCharts, setShowExamCharts] = useState(false)

  // Tab state
  const [activeTab, setActiveTab] = useState('students')
  const [examStudents, setExamStudents] = useState([])
  const [examSummary, setExamSummary] = useState(null)
  const [examLoading, setExamLoading] = useState(false)
  const [examLoaded, setExamLoaded] = useState(false)

  // Admin mode
  const [adminMode, setAdminMode] = useState(false)
  const [adminKey, setAdminKey] = useState('')
  const [showAdminInput, setShowAdminInput] = useState(false)
  const [adminError, setAdminError] = useState('')

  // Scheduler info
  const [schedulerInfo, setSchedulerInfo] = useState(null)

  // KPI filter state
  const [kpisFiltered, setKpisFiltered] = useState(false)

  // Allowlist state
  const [allowedUsers, setAllowedUsers] = useState([])
  const [allowlistLoading, setAllowlistLoading] = useState(false)
  const [showAllowlist, setShowAllowlist] = useState(false)
  const [newAllowEmail, setNewAllowEmail] = useState('')
  const [newAllowName, setNewAllowName] = useState('')
  const [allowlistError, setAllowlistError] = useState('')
  const [allowlistEnforcing, setAllowlistEnforcing] = useState(false)
  const [allowlistSaving, setAllowlistSaving] = useState(false)
  const [allowlistLoadError, setAllowlistLoadError] = useState('')
  const [allowlistSheetLoaded, setAllowlistSheetLoaded] = useState(true)

  // Multi-department state
  const [extraDepartments, setExtraDepartments] = useState([])
  const deptPrefsLoaded = useRef(false)
  const [showDeptManager, setShowDeptManager] = useState(false)
  const [deptInputValue, setDeptInputValue] = useState('')
  const [deptError, setDeptError] = useState('')
  const [treeLoading, setTreeLoading] = useState(false)
  // Dept chip wall collapses past this many chips — 70 tree-loaded depts
  // rendered as six rows of chips buried the actual dashboard.
  const [deptChipsExpanded, setDeptChipsExpanded] = useState(false)
  // Generation counter for fetchMultiDeptStudents — a new run (extras changed
  // mid-flight, e.g. Clear All during a load) invalidates in-flight batches.
  const multiFetchGen = useRef(0)
  // True while a multi-dept batch run owns the screen. Guards the plain
  // primary-dept fetches (fetchDashboardData / loadFullDataInBackground)
  // from overwriting the merged multi data if they resolve mid-stream —
  // without this the student list visibly SHRINKS back to primary-only for
  // a moment during a cold login.
  const multiActiveRef = useRef(false)
  // {loaded, total} while batches stream in — drives the small progress
  // indicator in the Department Manager header.
  const [multiProgress, setMultiProgress] = useState(null)
  const [departmentMeta, setDepartmentMeta] = useState([])
  const [studentDeptFilter, setStudentDeptFilter] = useState([])
  const [showDeptDropdown, setShowDeptDropdown] = useState(false)

  // Hidden students state
  const [hiddenStudents, setHiddenStudents] = useState([])
  const hiddenPrefsLoaded = useRef(false)
  const [showHidden, setShowHidden] = useState(false)

  // GHL integration state
  const [ghlSettings, setGhlSettings] = useState(null)
  const [showGhlSetup, setShowGhlSetup] = useState(false)
  const [ghlCalendars, setGhlCalendars] = useState([])
  const [ghlTokenInput, setGhlTokenInput] = useState('')
  const [ghlLocationInput, setGhlLocationInput] = useState('')
  const [ghlCalendarInput, setGhlCalendarInput] = useState('')
  const [ghlError, setGhlError] = useState('')
  const [ghlSaving, setGhlSaving] = useState(false)
  const [ghlFetchingCals, setGhlFetchingCals] = useState(false)

  // Bitrix24 integration state
  const [bitrixSettings, setBitrixSettings] = useState(null)
  const [showBitrixSetup, setShowBitrixSetup] = useState(false)
  const [bitrixWebhookInput, setBitrixWebhookInput] = useState('')
  const [bitrixError, setBitrixError] = useState('')
  const [bitrixSaving, setBitrixSaving] = useState(false)
  const [bitrixValidating, setBitrixValidating] = useState(false)
  const [bitrixValidated, setBitrixValidated] = useState(false)

  // User Google Sheet integration state
  const [sheetSettings, setSheetSettings] = useState(null)
  const [showSheetSetup, setShowSheetSetup] = useState(false)
  const [sheetUrlInput, setSheetUrlInput] = useState('')
  const [sheetError, setSheetError] = useState('')
  const [sheetSaving, setSheetSaving] = useState(false)
  const [sheetValidating, setSheetValidating] = useState(false)
  const [sheetValidated, setSheetValidated] = useState(false)
  const [sheetValidationInfo, setSheetValidationInfo] = useState(null)

  // Filtering and search
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [studentCourseFilter, setStudentCourseFilter] = useState('all')
  const [examResultFilter, setExamResultFilter] = useState('no-result')
  const [examCourseFilter, setExamCourseFilter] = useState('all')
  const [examDeptFilter, setExamDeptFilter] = useState([])
  const [showExamDeptDropdown, setShowExamDeptDropdown] = useState(false)
  const [examStateFilter, setExamStateFilter] = useState('all')
  const [examReadinessFilter, setExamReadinessFilter] = useState('all')
  const [examDaysFilter, setExamDaysFilter] = useState('all')

  // On mount WITHOUT initialData (page reload with a live session), do NOT
  // fetch here — the dept-prefs loader below drives the initial fetch after
  // prefs resolve, picking exactly ONE path: batched multi when extras
  // exist, primary-only otherwise. Firing the primary fetch here as well
  // duplicated it (the dept-length effect's initial run fires it too) and
  // raced it against the batch stream on a cold instance — the slowest
  // loser timed out and threw "Failed to load dashboard data" over a
  // healthy batch load.
  useEffect(() => {
    if (!initialData) {
      console.log('[DASHBOARD] Mounted without initial data — waiting for dept prefs to pick the load path')
    } else {
      console.log('[DASHBOARD] Using pre-fetched data')
      // If we got quick data, load full data in background
      if (initialData.isQuick) {
        console.log('[DASHBOARD] Loading full data in background...')
        loadFullDataInBackground()
      }
    }
  }, [])

  // Load department prefs from backend on mount, with a localStorage
  // fallback. On serverless hosting the backend prefs DB lives on ephemeral
  // /tmp and resets whenever a fresh instance spins up — without the
  // fallback, a page reload after instance rotation silently dropped every
  // added department (and if the primary dept is a container with all its
  // students in sub-departments, the dashboard read "No students found").
  // localStorage mirrors the list per-browser; the save effect below then
  // re-seeds the backend DB on the new instance.
  useEffect(() => {
    if (!user?.email) return
    const lsKey = `ji_extra_depts_${(user.email || '').toLowerCase()}`
    const loadDeptPrefs = async () => {
      // UNION the server list with the localStorage mirror. The server copy
      // lives in per-instance storage on Vercel — different instances hold
      // different stale versions, so "server wins" let whichever instance
      // answered SHRINK the list and then the save-back effect overwrote
      // the good mirror with the stale copy (the 853/860/1094-student
      // roulette of 2026-08-12: each login adopted a different partial
      // list, loaded it fully, and honestly said "done"). Union never
      // loses entries and the save-back heals the answering instance.
      // Clear All still empties both copies — that's an explicit save of
      // [], not this login-time merge.
      let serverList = []
      try {
        const res = await fetch(`${API_BASE}/dashboard/dept-prefs`, { credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          if (data.success && Array.isArray(data.departmentIds)) {
            serverList = data.departmentIds
          }
        }
      } catch (err) { console.error('[DEPT] Failed to load dept prefs:', err) }
      let mirror = []
      try {
        const cached = JSON.parse(localStorage.getItem(lsKey) || '[]')
        if (Array.isArray(cached)) mirror = cached
      } catch { /* corrupted localStorage — ignore */ }
      const seenIds = new Set()
      let loaded = []
      for (const id of [...serverList, ...mirror]) {
        const k = (typeof id === 'string' ? id : '').toLowerCase()
        if (k && !seenIds.has(k)) { seenIds.add(k); loaded.push(id) }
      }
      loaded = loaded.slice(0, MAX_EXTRA_DEPTS)
      if (mirror.length > serverList.length) {
        console.log(`[DEPT] Union restored ${loaded.length} dept(s) (server had ${serverList.length}, mirror had ${mirror.length})`)
      }
      deptPrefsLoaded.current = true
      if (loaded.length > 0) {
        // Setting extras triggers the dept-length effect → batched multi
        // load (batch 1 includes the primary dept, so nothing is missed).
        setExtraDepartments(loaded)
      } else if (!initialData) {
        // No extras and no login-time prefetch — this is the one path where
        // the primary-only fetch is the right (and only) loader.
        fetchDashboardData()
      }
    }
    loadDeptPrefs()
  }, [user?.email])

  // Save department prefs when they change (skip initial load) — to the
  // backend AND the localStorage mirror.
  useEffect(() => {
    if (!user?.email || !deptPrefsLoaded.current) return
    try {
      localStorage.setItem(
        `ji_extra_depts_${(user.email || '').toLowerCase()}`,
        JSON.stringify(extraDepartments)
      )
    } catch { /* storage full/blocked — backend save still runs */ }
    fetch(`${API_BASE}/dashboard/dept-prefs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ departmentIds: extraDepartments })
    }).catch(err => console.error('[DEPT] Failed to save dept prefs:', err))
  }, [extraDepartments])

  // Load hidden students from backend on mount
  useEffect(() => {
    if (!user?.email) return
    const loadHiddenPrefs = async () => {
      try {
        const res = await fetch(`${API_BASE}/dashboard/hidden-students`, { credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          if (data.success && data.hiddenEmails?.length > 0) {
            setHiddenStudents(data.hiddenEmails)
          }
        }
      } catch (err) { console.error('[HIDDEN] Failed to load hidden prefs:', err) }
      hiddenPrefsLoaded.current = true
    }
    loadHiddenPrefs()
  }, [user?.email])

  // Save hidden students to backend when they change (skip initial load)
  useEffect(() => {
    if (!user?.email || !hiddenPrefsLoaded.current) return
    fetch(`${API_BASE}/dashboard/hidden-students`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ hiddenEmails: hiddenStudents })
    }).catch(err => console.error('[HIDDEN] Failed to save hidden prefs:', err))
  }, [hiddenStudents])

  // Load GHL settings on mount
  useEffect(() => {
    if (!user?.email) return
    fetch(`${API_BASE}/dashboard/ghl-settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.success) setGhlSettings(d) })
      .catch(err => console.error('[GHL] Failed to load settings:', err))
  }, [user?.email])

  // Load Bitrix settings on mount
  useEffect(() => {
    if (!user?.email) return
    fetch(`${API_BASE}/dashboard/bitrix-settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.success) setBitrixSettings(d) })
      .catch(err => console.error('[Bitrix] Failed to load settings:', err))
  }, [user?.email])

  // Load User Sheet settings on mount
  useEffect(() => {
    if (!user?.email) return
    fetch(`${API_BASE}/dashboard/sheet-settings`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.success) setSheetSettings(d) })
      .catch(err => console.error('[Sheet] Failed to load settings:', err))
  }, [user?.email])

  // Re-fetch students when extra departments change
  useEffect(() => {
    if (extraDepartments.length > 0) {
      fetchMultiDeptStudents()
    } else {
      // This effect also runs once on mount with length 0 — before the
      // dept prefs have resolved. Firing the primary fetch then would race
      // (and duplicate) whichever loader the prefs loader picks. Only act
      // on a REAL transition back to single-dept (e.g. Clear All).
      if (!deptPrefsLoaded.current) return
      // Cancel any in-flight batch run and release its screen ownership —
      // a superseded run's finally skips the reset, so without this the
      // primary fetch below would be blocked from writing state forever.
      multiFetchGen.current++
      multiActiveRef.current = false
      setMultiProgress(null)
      // Reset department meta and filter when going back to single dept
      setDepartmentMeta([])
      setStudentDeptFilter([])
      fetchDashboardData()
    }
    // Invalidate exam data so it re-fetches with new departments
    setExamLoaded(false)
  }, [extraDepartments.length])

  // Fetch exam data when exam tab is selected for the first time
  useEffect(() => {
    if (activeTab === 'exam' && !examLoaded && !examLoading) {
      fetchExamData()
    }
  }, [activeTab])

  const fetchExamData = async (overrideAdminKey) => {
    setExamLoading(true)
    try {
      const key = overrideAdminKey || adminKey
      const params = new URLSearchParams()
      if (key) params.set('adminKey', key)
      if (extraDepartments.length > 0) params.set('departments', extraDepartments.join(','))
      const qs = params.toString()
      const url = qs ? `${API_BASE}/exam/students?${qs}` : `${API_BASE}/exam/students`
      console.log('[EXAM] Fetching with URL:', url, 'extraDepts:', extraDepartments)
      const res = await fetchWithAuthRetry(url, { credentials: 'include' })
      if (!res.ok) {
        if (res.status === 401) {
          onLogout()
          return
        }
        throw new Error('Failed to fetch exam data')
      }
      const data = await res.json()
      if (data.success) {
        setExamStudents(data.students)
        setExamSummary(data.examSummary)
        setKpisFiltered(false)
        setExamLoaded(true)

        // Fetch scheduler status in admin mode
        if (key) {
          fetch(`${API_BASE}/exam/sync-scheduler/status`, { credentials: 'include' })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.success) setSchedulerInfo(d.scheduler) })
            .catch(() => {})
        }
      }
    } catch (err) {
      setError('Failed to load exam data. Please try again.')
      console.error('[DASHBOARD] Exam data error:', err)
    } finally {
      setExamLoading(false)
    }
  }

  // Load full data with enrollments in background
  const loadFullDataInBackground = async () => {
    try {
      const [summaryRes, studentsRes] = await Promise.all([
        fetchWithAuthRetry(`${API_BASE}/dashboard/summary`, { credentials: 'include' }),
        fetchWithAuthRetry(`${API_BASE}/dashboard/students`, { credentials: 'include' })
      ])

      if (summaryRes.ok && studentsRes.ok) {
        const [summaryData, studentsData] = await Promise.all([
          summaryRes.json(),
          studentsRes.json()
        ])

        if (summaryData.success && studentsData.success && !multiActiveRef.current) {
          console.log('[DASHBOARD] Full data loaded, updating...')
          setSummary(summaryData.summary)
          setStudents(studentsData.students)
          setLastSynced(new Date())
        }
      }
    } catch (err) {
      console.error('[DASHBOARD] Background load failed:', err)
    } finally {
      setLoadingFull(false)
    }
  }

  // Batched, progressive multi-dept loader.
  //
  // The previous version sent ALL extra departments as one request. At real
  // scale (70-145 tree-loaded depts) that single request ran minutes on a
  // cold serverless instance and died at the platform's duration ceiling —
  // so a fresh login showed primary-only students until the user hit Sync
  // (whose server-side path happened to survive on part-warmed caches).
  // Batching keeps every request small enough to finish anywhere, and the
  // dashboard fills progressively as each batch lands instead of blocking
  // on the slowest department.
  const MULTI_BATCH_SIZE = 15

  const computeClientSummary = (studs) => {
    const total = studs.length
    const count = (st) => studs.filter(s => s.status?.status === st).length
    const totalProgress = studs.reduce((acc, s) => acc + (s.progress?.value || 0), 0)
    return {
      totalStudents: total,
      completeCount: count('COMPLETE'),
      activeCount: count('ACTIVE'),
      warningCount: count('WARNING'),
      reengageCount: count('RE-ENGAGE'),
      averageProgress: total > 0 ? Math.round(totalProgress / total * 10) / 10 : 0,
    }
  }

  const fetchMultiDeptStudents = async () => {
    const gen = ++multiFetchGen.current
    multiActiveRef.current = true
    setLoading(true)
    setError(null)

    const fetchBatch = async (batchIds) => {
      const url = `${API_BASE}/dashboard/students/multi?departments=${encodeURIComponent(batchIds.join(','))}`
      const res = await fetchWithAuthRetry(url, { credentials: 'include' })
      if (!res.ok) {
        if (res.status === 401) return { auth: true }
        throw new Error(`Batch failed with HTTP ${res.status}`)
      }
      let data = await res.json()
      // Server reports the token died mid-batch (it never mints its own
      // replacement — see ensureFreshToken). Refresh once and refire this
      // batch with the new cookie; already-ok depts re-serve from cache.
      if (data?.success && data.tokenExpired) {
        if (await ensureFreshToken()) {
          const res2 = await fetchWithAuthRetry(url, { credentials: 'include' })
          if (res2.ok) data = await res2.json()
        }
      }
      return { data }
    }

    // Merge helpers — the primary dept rides along in EVERY batch response
    // (the backend always includes it), so dedupe students by id and keep
    // the first meta entry per dept id.
    const byId = new Map()
    const metaById = new Map()
    const mergeBatch = (data) => {
      for (const s of (data.students || [])) {
        if (s.id && !byId.has(s.id)) byId.set(s.id, s)
      }
      for (const m of (data.departments || [])) {
        if (m.id && !metaById.has(m.id)) metaById.set(m.id, m)
        else if (m.id && m.status === 'ok' && metaById.get(m.id)?.status === 'error') {
          metaById.set(m.id, m)  // a later success beats an earlier failure
        } else if (m.id && m.status === 'ok' && !m.partial && metaById.get(m.id)?.partial) {
          metaById.set(m.id, m)  // a later FULL fetch beats an earlier partial
        }
      }
    }
    const pushMerged = () => {
      const merged = Array.from(byId.values())
      merged.sort((a, b) =>
        (a.status?.priority || 0) - (b.status?.priority || 0) ||
        (b.progress?.value || 0) - (a.progress?.value || 0))
      setStudents(merged)
      setSummary(computeClientSummary(merged))
      setDepartmentMeta(Array.from(metaById.values()))
    }

    try {
      const batches = []
      for (let i = 0; i < extraDepartments.length; i += MULTI_BATCH_SIZE) {
        batches.push(extraDepartments.slice(i, i + MULTI_BATCH_SIZE))
      }
      console.log(`[DASHBOARD] Multi-dept: ${extraDepartments.length} dept(s) in ${batches.length} batch(es)`)

      // Paint policy ("loads like the tree button, not one dept at a time"):
      // batch 1 paints the table immediately so there's no dead spinner; the
      // remaining batches buffer silently behind a progress counter (only
      // the dept-chip bar grows per batch — cheap, non-disruptive), and the
      // table/KPIs settle ONCE at the end. Per-batch full repaints made rows
      // reshuffle, KPIs bounce, and pagination reset on every wave.
      for (let b = 0; b < batches.length; b++) {
        if (gen !== multiFetchGen.current) return  // superseded by a newer run
        const { auth, data } = await fetchBatch(batches[b])
        if (auth) { onLogout(); return }
        if (gen !== multiFetchGen.current) return
        if (data?.success) {
          mergeBatch(data)
          if (b === 0) {
            pushMerged()
            setLoading(false)  // show data as soon as batch 1 lands
          } else {
            setDepartmentMeta(Array.from(metaById.values()))
          }
          setMultiProgress({
            loaded: Math.min((b + 1) * MULTI_BATCH_SIZE, extraDepartments.length),
            total: extraDepartments.length,
          })
        }
      }

      // Sweep passes for depts that failed with token-expired errors.
      // Up to 3 passes with backoff: a refresh-during-load can leave an
      // abandoned server request warring over the account token for up to
      // ~a minute (its mint revokes ours, ours revokes its). One immediate
      // pass loses that war; waiting out the zombie and re-asking wins it —
      // the LAST active requester ends up holding the live token.
      const SWEEP_DELAYS_MS = [0, 4000, 10000]
      // Retryable = token-expired errors AND partial loads (a dept whose
      // fetch was interrupted returns 'ok' with fewer students + partial
      // flag; partials are never cached, so a retry refetches for real).
      const listExpired = () => Array.from(metaById.values())
        .filter(m => (m.status === 'error' && /session expired|authoriz/i.test(m.error || '')) || m.partial === true)
        .map(m => m.id)
        .filter(id => extraDepartments.some(d => d.toLowerCase() === (id || '').toLowerCase())
          || (id || '').toLowerCase() === (department?.id || '').toLowerCase())
      for (let pass = 0; pass < SWEEP_DELAYS_MS.length; pass++) {
        const failedExpired = listExpired()
        if (failedExpired.length === 0 || gen !== multiFetchGen.current) break
        if (SWEEP_DELAYS_MS[pass] > 0) {
          await new Promise(r => setTimeout(r, SWEEP_DELAYS_MS[pass]))
          if (gen !== multiFetchGen.current) break
        }
        console.log(`[DASHBOARD] Multi-dept sweep ${pass + 1}/${SWEEP_DELAYS_MS.length}: retrying ${failedExpired.length} expired dept(s)`)
        try {
          const { auth, data } = await fetchBatch(failedExpired)
          if (auth) { onLogout(); return }
          if (data?.success && gen === multiFetchGen.current) {
            for (const m of (data.departments || [])) {
              if (m.id) metaById.set(m.id, m)
            }
            mergeBatch(data)
            pushMerged()
          }
        } catch (err) {
          console.error(`[DASHBOARD] Sweep pass ${pass + 1} failed:`, err)
        }
      }

      if (gen !== multiFetchGen.current) return
      pushMerged()  // the single final settle — everything sorted, one repaint
      setLastSynced(new Date())
      const stillFailed = Array.from(metaById.values()).filter(m => m.status === 'error')
      if (stillFailed.length > 0) {
        const expiredLeft = stillFailed.filter(m => /session expired|authoriz/i.test(m.error || ''))
        if (expiredLeft.length === stillFailed.length) {
          // Token-turbulence residue — never show users a "Session expired"
          // wall. Keep healing quietly in the background (15s/30s/60s) and
          // only escalate to an actionable message if every pass loses.
          setDeptError(`${stillFailed.length} department(s) still syncing — retrying automatically…`)
          ;(async () => {
            for (const delay of [15000, 30000, 60000]) {
              await new Promise(r => setTimeout(r, delay))
              if (gen !== multiFetchGen.current) return
              const ids = listExpired()
              if (ids.length === 0) { setDeptError(''); return }
              try {
                const { auth, data } = await fetchBatch(ids)
                if (auth) { onLogout(); return }
                if (data?.success && gen === multiFetchGen.current) {
                  for (const m of (data.departments || [])) {
                    if (m.id) metaById.set(m.id, m)
                  }
                  mergeBatch(data)
                  pushMerged()
                  const left = listExpired()
                  if (left.length === 0) {
                    setDeptError('')
                    setLastSynced(new Date())
                    return
                  }
                  setDeptError(`${left.length} department(s) still syncing — retrying automatically…`)
                }
              } catch (e) {
                console.error('[DASHBOARD] Background heal pass failed:', e)
              }
            }
            if (gen === multiFetchGen.current && listExpired().length > 0) {
              setDeptError(`${listExpired().length} department(s) could not load — press Sync to retry.`)
            }
          })()
        } else {
          // Real (non-token) failures: one concise line, not a repeated wall.
          const firstErr = stillFailed[0].error || stillFailed[0].id
          setDeptError(`Could not load ${stillFailed.length} department(s): ${firstErr}${stillFailed.length > 1 ? ` (+${stillFailed.length - 1} more)` : ''}`)
        }
      } else {
        const stillPartial = Array.from(metaById.values()).filter(m => m.partial)
        if (stillPartial.length > 0) {
          const missing = stillPartial.reduce((a, m) => a + (m.missing || 0), 0)
          setDeptError(`${stillPartial.length} department(s) loaded partially${missing ? ` (~${missing} students missing)` : ''} — press Sync to retry.`)
        }
      }
    } catch (err) {
      if (gen !== multiFetchGen.current) return
      // Paint whatever batches accumulated — a mid-run failure should
      // degrade to partial data, not wipe the screen.
      pushMerged()
      setError('Some departments failed to load. Sync will retry them.')
      console.error('[DASHBOARD] Multi-dept error:', err)
    } finally {
      if (gen === multiFetchGen.current) {
        setLoading(false)
        setMultiProgress(null)
        multiActiveRef.current = false
      }
    }
  }

  const handleAddDepartment = () => {
    const guidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
    // Split on newlines, commas, or whitespace — supports paste-multiple
    const tokens = deptInputValue.split(/[\s,]+/).map(t => t.trim()).filter(Boolean)

    if (tokens.length === 0) {
      setDeptError('Enter at least one Department ID')
      return
    }

    const primary = department?.id?.toLowerCase()
    const existing = new Set(extraDepartments.map(d => d.toLowerCase()))
    const toAdd = []
    const invalid = []
    let duplicates = 0
    let isPrimary = 0
    let overflow = 0
    const remainingSlots = MAX_EXTRA_DEPTS - extraDepartments.length

    for (const t of tokens) {
      if (!guidPattern.test(t)) {
        invalid.push(t)
        continue
      }
      const low = t.toLowerCase()
      if (low === primary) {
        isPrimary++
        continue
      }
      if (existing.has(low) || toAdd.some(a => a.toLowerCase() === low)) {
        duplicates++
        continue
      }
      if (toAdd.length >= remainingSlots) {
        overflow++
        continue
      }
      toAdd.push(t)
    }

    if (toAdd.length === 0) {
      const reasons = []
      if (invalid.length) reasons.push(`${invalid.length} invalid`)
      if (duplicates) reasons.push(`${duplicates} duplicate`)
      if (isPrimary) reasons.push(`${isPrimary} primary`)
      if (overflow) reasons.push(`${overflow} over limit (${MAX_EXTRA_DEPTS} max)`)
      setDeptError(`Nothing added — ${reasons.join(', ') || 'no valid IDs found'}`)
      return
    }

    const skipped = invalid.length + duplicates + isPrimary + overflow
    if (skipped > 0) {
      const reasons = []
      if (invalid.length) reasons.push(`${invalid.length} invalid`)
      if (duplicates) reasons.push(`${duplicates} duplicate`)
      if (isPrimary) reasons.push(`${isPrimary} primary`)
      if (overflow) reasons.push(`${overflow} over limit`)
      setDeptError(`Added ${toAdd.length}. Skipped: ${reasons.join(', ')}`)
    } else {
      setDeptError('')
    }
    setDeptInputValue('')
    setExtraDepartments(prev => [...prev, ...toAdd])
  }

  const handleRemoveDepartment = (id) => {
    setExtraDepartments(prev => prev.filter(d => d !== id))
  }

  // Clear ALL added departments at once (primary stays). Setting the list
  // empty flows through the same effects as per-chip removal: prefs save
  // (backend + localStorage mirror both persist the empty list) and the
  // student reload reverts to primary-only. One confirm guards against a
  // misclick nuking a hand-curated list — a tree-loaded list is one
  // Load Dept Tree click to restore, but a pasted-GUID list is not.
  const handleClearDepartments = () => {
    if (extraDepartments.length === 0) return
    if (!window.confirm(
      `Remove all ${extraDepartments.length} added department(s)? ` +
      `Your primary department stays loaded. Load Dept Tree can re-add a tree in one click.`
    )) return
    setExtraDepartments([])
    setDeptError('')
  }

  // Fetch every sub-department under the primary dept from the backend tree
  // walk and add them all at once — same dedupe/cap rules as manual adds, so
  // the downstream prefs-save and /students/multi flows are identical.
  const handleLoadDeptTree = async () => {
    setTreeLoading(true)
    setDeptError('')
    try {
      const res = await fetchWithAuthRetry(`${API_BASE}/dashboard/dept-tree`, { credentials: 'include' })
      const data = await res.json()
      if (!data.success) {
        setDeptError(data.error || 'Failed to load department tree')
        return
      }
      const primary = department?.id?.toLowerCase()
      const existing = new Set(extraDepartments.map(d => d.toLowerCase()))
      const toAdd = []
      let overflow = 0
      const remainingSlots = MAX_EXTRA_DEPTS - extraDepartments.length
      for (const d of (data.departments || [])) {
        const low = (d.id || '').toLowerCase()
        if (!low || low === primary || existing.has(low) || toAdd.some(a => a.toLowerCase() === low)) continue
        if (toAdd.length >= remainingSlots) { overflow++; continue }
        toAdd.push(d.id)
      }
      if (data.count === 0) {
        setDeptError('No sub-departments found under your primary department')
      } else if (toAdd.length === 0) {
        setDeptError(overflow
          ? `All ${MAX_EXTRA_DEPTS} department slots are full — ${overflow} sub-department(s) skipped`
          : `All ${data.count} sub-department(s) are already loaded`)
      } else {
        const notes = []
        if (overflow) notes.push(`${overflow} skipped (${MAX_EXTRA_DEPTS} dept limit)`)
        if (data.truncated) notes.push('tree truncated at 200 nodes')
        setDeptError(notes.length ? `Added ${toAdd.length} sub-department(s). ${notes.join('; ')}` : '')
        setExtraDepartments(prev => [...prev, ...toAdd])
      }
    } catch (err) {
      console.error('[DEPT-TREE] Failed:', err)
      setDeptError('Failed to load department tree')
    } finally {
      setTreeLoading(false)
    }
  }

  const handleHideStudent = (studentEmail) => {
    const normalized = studentEmail.toLowerCase().trim()
    setHiddenStudents(prev => {
      if (prev.includes(normalized)) return prev
      return [...prev, normalized]
    })
  }

  const handleUnhideStudent = (studentEmail) => {
    const normalized = studentEmail.toLowerCase().trim()
    setHiddenStudents(prev => prev.filter(e => e !== normalized))
  }

  // GHL handlers
  const handleFetchGhlCalendars = async () => {
    if (!ghlTokenInput.trim() || !ghlLocationInput.trim()) {
      setGhlError('Token and Location ID are required')
      return
    }
    setGhlFetchingCals(true)
    setGhlError('')
    try {
      const params = new URLSearchParams({ token: ghlTokenInput.trim(), location_id: ghlLocationInput.trim() })
      const res = await fetch(`${API_BASE}/dashboard/ghl-calendars?${params}`, { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setGhlCalendars(data.calendars || [])
        if (data.calendars?.length === 0) setGhlError('No calendars found for this location')
      } else {
        setGhlError(data.error || 'Failed to fetch calendars')
      }
    } catch (err) {
      setGhlError('Failed to connect to GHL API')
    } finally {
      setGhlFetchingCals(false)
    }
  }

  const handleSaveGhlSettings = async (enabled) => {
    setGhlSaving(true)
    setGhlError('')
    try {
      const body = {
        enabled,
        ghl_token: ghlTokenInput.trim() || undefined,
        location_id: ghlLocationInput.trim() || undefined,
        calendar_id: ghlCalendarInput || undefined,
      }
      const res = await fetch(`${API_BASE}/dashboard/ghl-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (data.success) {
        setGhlSettings(data)
        if (enabled) {
          // GHL enabled — disable Bitrix and User Sheet on frontend too
          setBitrixSettings(prev => prev ? { ...prev, enabled: false } : prev)
          setSheetSettings(prev => prev ? { ...prev, enabled: false } : prev)
          setShowGhlSetup(false)
          setExamLoaded(false)
          if (activeTab === 'exam') fetchExamData()
        } else {
          setExamLoaded(false)
          if (activeTab === 'exam') fetchExamData()
        }
      } else {
        setGhlError(data.error || 'Failed to save settings')
      }
    } catch (err) {
      setGhlError('Failed to save GHL settings')
    } finally {
      setGhlSaving(false)
    }
  }

  // Bitrix handlers
  const handleValidateBitrix = async () => {
    if (!bitrixWebhookInput.trim()) {
      setBitrixError('Webhook URL is required')
      return
    }
    setBitrixValidating(true)
    setBitrixError('')
    setBitrixValidated(false)
    try {
      const params = new URLSearchParams({ webhook_url: bitrixWebhookInput.trim() })
      const res = await fetch(`${API_BASE}/dashboard/bitrix-validate?${params}`, { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setBitrixValidated(true)
        setBitrixError('')
      } else {
        setBitrixError(data.error || 'Validation failed')
      }
    } catch (err) {
      setBitrixError('Failed to connect to Bitrix API')
    } finally {
      setBitrixValidating(false)
    }
  }

  const handleSaveBitrixSettings = async (enabled) => {
    setBitrixSaving(true)
    setBitrixError('')
    try {
      const body = {
        enabled,
        webhook_url: bitrixWebhookInput.trim() || undefined,
      }
      const res = await fetch(`${API_BASE}/dashboard/bitrix-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (data.success) {
        setBitrixSettings(data)
        if (enabled) {
          // Bitrix enabled — disable GHL and User Sheet on frontend too
          setGhlSettings(prev => prev ? { ...prev, enabled: false } : prev)
          setSheetSettings(prev => prev ? { ...prev, enabled: false } : prev)
          setShowBitrixSetup(false)
          setExamLoaded(false)
          if (activeTab === 'exam') fetchExamData()
        } else {
          setExamLoaded(false)
          if (activeTab === 'exam') fetchExamData()
        }
      } else {
        setBitrixError(data.error || 'Failed to save settings')
      }
    } catch (err) {
      setBitrixError('Failed to save Bitrix settings')
    } finally {
      setBitrixSaving(false)
    }
  }

  // ── User Google Sheet handlers ──────────────────────────────────
  const handleValidateSheet = async () => {
    setSheetValidating(true)
    setSheetError('')
    setSheetValidated(false)
    setSheetValidationInfo(null)
    try {
      const params = new URLSearchParams({ sheet_url: sheetUrlInput.trim() })
      const res = await fetch(`${API_BASE}/dashboard/sheet-validate?${params}`, { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setSheetValidated(true)
        setSheetValidationInfo({ row_count: data.row_count, columns: data.columns })
      } else {
        setSheetError(data.error || 'Sheet validation failed')
      }
    } catch (err) {
      setSheetError('Failed to validate sheet')
    } finally {
      setSheetValidating(false)
    }
  }

  const handleSaveSheetSettings = async (enabled) => {
    setSheetSaving(true)
    setSheetError('')
    try {
      const body = {
        enabled,
        sheet_url: sheetUrlInput.trim() || undefined,
      }
      const res = await fetch(`${API_BASE}/dashboard/sheet-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (data.success) {
        setSheetSettings(data)
        if (enabled) {
          // Sheet enabled — disable GHL and Bitrix on frontend too
          setGhlSettings(prev => prev ? { ...prev, enabled: false } : prev)
          setBitrixSettings(prev => prev ? { ...prev, enabled: false } : prev)
          setShowSheetSetup(false)
          setExamLoaded(false)
          if (activeTab === 'exam') fetchExamData()
        } else {
          setExamLoaded(false)
          if (activeTab === 'exam') fetchExamData()
        }
      } else {
        setSheetError(data.error || 'Failed to save settings')
      }
    } catch (err) {
      setSheetError('Failed to save sheet settings')
    } finally {
      setSheetSaving(false)
    }
  }

  const fetchDashboardData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch summary and students in parallel
      const [summaryRes, studentsRes] = await Promise.all([
        fetchWithAuthRetry(`${API_BASE}/dashboard/summary`, { credentials: 'include' }),
        fetchWithAuthRetry(`${API_BASE}/dashboard/students`, { credentials: 'include' })
      ])

      if (!summaryRes.ok || !studentsRes.ok) {
        if (summaryRes.status === 401 || studentsRes.status === 401) {
          onLogout()
          return
        }
        throw new Error('Failed to fetch data')
      }

      const summaryData = await summaryRes.json()
      const studentsData = await studentsRes.json()

      // A multi-dept batch run owns the screen while active — this
      // primary-only result would visibly SHRINK the merged list if it
      // resolved mid-stream (the server-side cache warm still happened,
      // which is all this call contributes in that case).
      if (multiActiveRef.current) return

      if (summaryData.success) {
        setSummary(summaryData.summary)
      }

      if (studentsData.success) {
        setStudents(studentsData.students)
      }

      setLastSynced(new Date())
    } catch (err) {
      // Never paint this banner over an active batch run — its data is
      // healthy; this primary-only call failing is irrelevant to the user.
      if (multiActiveRef.current) return
      setError('Failed to load dashboard data. Please try again.')
      console.error('Dashboard error:', err)
    } finally {
      setLoading(false)
    }
  }, [onLogout])

  const handleSync = async (_isRetry = false) => {
    setSyncing(true)
    setError(null)

    try {
      // With extras, Sync asks the server to invalidate every dept cache but
      // fetch only the primary — a 70-dept sequential sync in one request
      // outlives Vercel's router and its orphaned lambda then wars over the
      // account token with the next reload. The extras re-stream through the
      // batched loader below (bounded, zombie-fenced).
      const body = extraDepartments.length > 0
        ? JSON.stringify({ extraDepartments, invalidateOnly: true })
        : undefined

      const response = await fetchWithAuthRetry(`${API_BASE}/dashboard/sync`, {
        method: 'POST',
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : {},
        body,
      })

      if (!response.ok) {
        if (response.status === 401) {
          onLogout()
          return
        }
        const errData = await response.json().catch(() => null)
        throw new Error(errData?.error || `Sync failed (${response.status})`)
      }

      const data = await response.json()

      if (data.success) {
        if (extraDepartments.length > 0) {
          // invalidateOnly path: server cleared all dept caches + refreshed
          // the primary. Re-stream everything through the batched loader —
          // it owns the merge, the progress counter, and the sweep retries.
          setLastSynced(new Date())
          if (examLoaded) {
            setExamLoaded(false)
            if (activeTab === 'exam') fetchExamData()
          }
          fetchMultiDeptStudents()
          return
        }
        // Token died mid-sync: refresh once (single-flight) and re-sync.
        if (data.tokenExpired && !_isRetry) {
          if (await ensureFreshToken()) {
            setTimeout(() => { handleSync(true) }, 500)
            return
          }
        }
        // Silent auto-retry on PARTIAL failures only — see fetchMultiDeptStudents.
        // Same gate: at least one dept ok AND at least one expired-error AND
        // we haven't retried. NOT on count==0 (legitimate empty).
        const _failed = (data.departments || []).filter(d => d.status === 'error')
        const _okCount = (data.departments || []).filter(d => d.status === 'ok').length
        const _expiredCount = _failed.filter(d => /session expired|authoriz/i.test(d.error || '')).length
        const _isPartial = _okCount > 0 && _failed.length > 0
        if (!_isRetry && _isPartial && _expiredCount > 0) {
          console.log('[SYNC] Partial — silent auto-retry in 1.5s')
          setTimeout(() => { handleSync(true) }, 1500)
          return
        }
        setSummary(data.summary)
        setStudents(data.students)
        if (data.departments) setDepartmentMeta(data.departments)
        setLastSynced(new Date())
        // Also refresh exam data if it was loaded
        if (examLoaded) {
          setExamLoaded(false)
          if (activeTab === 'exam') {
            fetchExamData()
          }
        }
      }
    } catch (err) {
      console.error('[SYNC] Error:', err)
      setError(err.message || 'Failed to sync data. Please try again.')
    } finally {
      setSyncing(false)
    }
  }

  const handleExport = async () => {
    try {
      const exportUrl = extraDepartments.length > 0
        ? `${API_BASE}/dashboard/export?departments=${encodeURIComponent(extraDepartments.join(','))}`
        : `${API_BASE}/dashboard/export`
      const response = await fetchWithAuthRetry(exportUrl, {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Export failed')
      }

      // Download the CSV
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `students_export_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      a.remove()
    } catch (err) {
      setError('Failed to export data. Please try again.')
    }
  }

  const handleAdminLogin = async (password) => {
    try {
      const res = await fetch(`${API_BASE}/exam/admin-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password })
      })
      const data = await res.json()
      if (data.success) {
        setAdminMode(true)
        setAdminKey(password)
        setShowAdminInput(false)
        setAdminError('')
        // Re-fetch exam data with admin key to get tracking data
        setExamLoaded(false)
        fetchExamData(password)
        // Load allowlist data
        fetchAllowlist(password)
      } else {
        setAdminError('Invalid password')
      }
    } catch {
      setAdminError('Failed to verify')
    }
  }

  const handleAdminLogout = () => {
    setAdminMode(false)
    setAdminKey('')
    setShowAdminInput(false)
    setAdminError('')
    setAllowedUsers([])
    setAllowlistEnforcing(false)
    setShowAllowlist(false)
    // Re-fetch without admin data
    setExamLoaded(false)
    fetchExamData('')
  }

  // ── Allowlist handlers ──────────────────────────────────
  // adminKey travels in the X-Admin-Key header, never the URL — the old
  // GET put the raw admin password in the query string (access logs,
  // browser history).
  const fetchAllowlist = async (key) => {
    setAllowlistLoading(true)
    setAllowlistLoadError('')
    try {
      const res = await fetch(`${API_BASE}/exam/allowlist`, {
        credentials: 'include',
        headers: { 'X-Admin-Key': key || adminKey }
      })
      const data = await res.json()
      if (data.success) {
        setAllowedUsers(data.users)
        setAllowlistEnforcing(data.enforcing)
        setAllowlistSheetLoaded(data.sheetLoaded !== false)
      } else {
        // Previously silent — a failed load rendered as "No users in
        // allowlist yet.", which could be a lie.
        setAllowlistLoadError(data.error || 'Failed to load allowlist')
      }
    } catch (err) {
      console.error('Failed to fetch allowlist:', err)
      setAllowlistLoadError('Network error loading allowlist')
    } finally {
      setAllowlistLoading(false)
    }
  }

  const handleAddAllowedUser = async () => {
    if (allowlistSaving) return  // double-Enter / double-click guard
    if (!newAllowEmail.trim()) {
      setAllowlistError('Email is required')
      return
    }
    setAllowlistError('')
    setAllowlistSaving(true)
    try {
      const res = await fetch(`${API_BASE}/exam/allowlist/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        credentials: 'include',
        body: JSON.stringify({ email: newAllowEmail.trim(), name: newAllowName.trim() })
      })
      const data = await res.json()
      if (data.success) {
        setAllowedUsers(data.users)
        setAllowlistEnforcing(true)
        setNewAllowEmail('')
        setNewAllowName('')
        if (data.warning) setAllowlistError(data.warning)
        if (data.autoAddedAdmin) {
          alert('Your admin account was automatically added to the allowlist to prevent lockout.')
        }
      } else {
        setAllowlistError(data.error || 'Failed to add user')
      }
    } catch {
      setAllowlistError('Network error')
    } finally {
      setAllowlistSaving(false)
    }
  }

  const handleRemoveAllowedUser = async (email) => {
    if (allowlistSaving) return
    if (!confirm(`Remove ${email} from allowed users? They will not be able to log in.`)) return
    setAllowlistSaving(true)
    try {
      const res = await fetch(`${API_BASE}/exam/allowlist/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        credentials: 'include',
        body: JSON.stringify({ email })
      })
      const data = await res.json()
      if (data.success) {
        setAllowedUsers(data.users)
        setAllowlistEnforcing(data.enforcing)
        if (data.warning) alert(data.warning)
      } else {
        // Previously silent — a failed remove looked like the button did nothing.
        setAllowlistError(data.error || 'Failed to remove user')
      }
    } catch (err) {
      console.error('Failed to remove from allowlist:', err)
      setAllowlistError('Network error removing user')
    } finally {
      setAllowlistSaving(false)
    }
  }

  const formatMins = (m) => m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`

  const calculateFullKPIs = (students) => {
    const total = students.length
    const passedStudents = students.filter(s => (s.passFail || '').toUpperCase() === 'PASS')
    const failedStudents = students.filter(s => (s.passFail || '').toUpperCase() === 'FAIL')
    const passed = passedStudents.length
    const failed = failedStudents.length
    const now = Date.now()
    let upcoming = 0, atRisk = 0
    students.forEach(s => {
      const dt = parseExamDateRaw(s.examDateRaw)
      const hasResult = (s.passFail || '').trim() !== ''
      if (dt > now && !hasResult) {
        upcoming++
        if (s.matched !== false && (s.progress?.value || 0) < 80) atRisk++
      }
    })
    const completedExams = passed + failed
    const passRate = completedExams > 0 ? Math.round(passed / completedExams * 1000) / 10 : 0

    // Study time analytics (matched students only)
    const matched = students.filter(s => s.matched !== false)
    const matchedPassed = passedStudents.filter(s => s.matched !== false)
    const matchedFailed = failedStudents.filter(s => s.matched !== false)

    const studyMins = (s) => ((s.timeSpent?.minutes || 0) + (s.examPrepTime?.minutes || 0))

    const avgStudyTime = matched.length > 0 ? Math.round(matched.reduce((sum, s) => sum + studyMins(s), 0) / matched.length) : 0
    const avgStudyPassed = matchedPassed.length > 0 ? Math.round(matchedPassed.reduce((sum, s) => sum + studyMins(s), 0) / matchedPassed.length) : 0
    const avgStudyFailed = matchedFailed.length > 0 ? Math.round(matchedFailed.reduce((sum, s) => sum + studyMins(s), 0) / matchedFailed.length) : 0
    const averageProgress = matched.length > 0 ? Math.round(matched.reduce((sum, s) => sum + (s.progress?.value || 0), 0) / matched.length * 10) / 10 : 0

    // Course type breakdown
    const courseTypes = {}
    students.forEach(s => {
      const course = (s.examCourse || 'Unknown').trim()
      if (!courseTypes[course]) courseTypes[course] = { total: 0, passed: 0, failed: 0 }
      courseTypes[course].total++
      const pf = (s.passFail || '').toUpperCase()
      if (pf === 'PASS') courseTypes[course].passed++
      else if (pf === 'FAIL') courseTypes[course].failed++
    })

    return {
      total, passed, failed, upcoming, atRisk, passRate,
      noResult: total - passed - failed - upcoming,
      avgStudyTime, avgStudyTimeFormatted: formatMins(avgStudyTime),
      avgStudyPassed, avgStudyPassedFormatted: formatMins(avgStudyPassed),
      avgStudyFailed, avgStudyFailedFormatted: formatMins(avgStudyFailed),
      averageProgress, courseTypes
    }
  }

  const handleUpdateResult = async (email, result) => {
    try {
      const res = await fetchWithAuthRetry(`${API_BASE}/exam/update-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, result, adminKey })
      })
      const data = await res.json()
      if (data.success) {
        // Update local state immediately
        const updated = examStudents.map(s =>
          s.email === email ? { ...s, passFail: result } : s
        )
        setExamStudents(updated)
        // Recalculate KPIs
        setExamSummary(calculateFullKPIs(updated))
        // Also update selectedStudent if viewing this student
        setSelectedStudent(prev =>
          prev && prev.email === email ? { ...prev, passFail: result } : prev
        )
        if (!data.sheetSaved && !data.ghlSaved && !data.bitrixSaved) {
          alert('Warning: Result saved locally but failed to save to data source. It may be lost after server restart.')
        }
      } else {
        alert('Failed to save result: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Failed to update result:', err)
      alert('Failed to save result. Check console for details.')
    }
  }

  const handleUpdateExamDate = async (email, newDate, newTime) => {
    try {
      const res = await fetchWithAuthRetry(`${API_BASE}/exam/update-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, date: newDate, time: newTime || '', adminKey })
      })
      const data = await res.json()
      if (data.success) {
        // Format date for display (e.g., "Jan 15, 2026")
        // Parse date as local time to avoid timezone shift
        const [year, month, day] = newDate.split('-').map(Number)
        const dt = new Date(year, month - 1, day)
        const formatted = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

        // Update local state immediately
        const updated = examStudents.map(s =>
          s.email === email ? {
            ...s,
            examDateRaw: newDate,
            examDate: formatted,
            examTime: newTime || s.examTime
          } : s
        )
        setExamStudents(updated)
        // Recalculate KPIs (upcoming, at-risk depend on exam date)
        setExamSummary(calculateFullKPIs(updated))
        // Also update selectedStudent if viewing this student
        setSelectedStudent(prev =>
          prev && prev.email === email ? {
            ...prev,
            examDateRaw: newDate,
            examDate: formatted,
            examTime: newTime || prev.examTime
          } : prev
        )
        if (!data.sheetSaved && !data.ghlSaved && !data.bitrixSaved) {
          alert('Warning: Date saved locally but failed to save to data source. It may be lost after server restart.')
        }
      } else {
        alert('Failed to save date: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Failed to update exam date:', err)
      alert('Failed to save date. Check console for details.')
    }
  }

  const handleUpdateStudentContact = async (studentId, contactData) => {
    try {
      const res = await fetchWithAuthRetry(`${API_BASE}/students/${studentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(contactData)
      })
      const data = await res.json()
      if (data.success) {
        const updated = data.student
        // Update students list
        setStudents(prev => prev.map(s =>
          s.id === studentId ? {
            ...s,
            firstName: updated.firstName,
            lastName: updated.lastName,
            fullName: `${updated.firstName} ${updated.lastName}`.trim(),
            email: updated.emailAddress,
            phone: updated.phone
          } : s
        ))
        return { success: true, student: updated }
      } else {
        return { success: false, error: data.error || 'Update failed' }
      }
    } catch (err) {
      console.error('Failed to update student contact:', err)
      return { success: false, error: 'Network error' }
    }
  }

  const handleUpdateSheetContact = async (email, name, newEmail, phone) => {
    try {
      const res = await fetch(`${API_BASE}/exam/update-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, name, newEmail, phone, adminKey })
      })
      const data = await res.json()
      if (data.success) {
        // Update local state
        const updated = examStudents.map(s =>
          s.email === email ? {
            ...s,
            fullName: name || s.fullName,
            email: newEmail || s.email,
            phone: phone || s.phone,
            sheetTracking: { ...s.sheetTracking, phone: phone || s.sheetTracking?.phone }
          } : s
        )
        setExamStudents(updated)
        // Update selected student
        setSelectedStudent(prev =>
          prev && prev.email === email ? {
            ...prev,
            fullName: name || prev.fullName,
            email: newEmail || prev.email,
            phone: phone || prev.phone,
            sheetTracking: { ...prev.sheetTracking, phone: phone || prev.sheetTracking?.phone }
          } : prev
        )
        if (!data.sheetSaved && !data.ghlSaved && !data.bitrixSaved) {
          alert('Warning: Contact saved locally but failed to save to data source.')
        }
      } else {
        alert('Failed to save contact: ' + (data.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Failed to update sheet contact:', err)
      alert('Failed to save contact. Check console for details.')
    }
  }

  // Build unique department names for Students tab filter
  const studentDepartments = [...new Set(
    students.map(s => (s.departmentName || '').trim()).filter(Boolean)
  )].sort()

  // Build unique course names for Students tab filter
  const studentCourseTypes = [...new Set(
    students.map(s => (s.courseName || '').trim()).filter(c => c && c !== 'No Course')
  )].sort()

  // Filter students (works for both tabs)
  const filteredStudents = students.filter(student => {
    // Hidden student filter
    const isHidden = hiddenStudents.includes((student.email || '').toLowerCase().trim())
    if (showHidden) return isHidden
    if (isHidden) return false

    const searchLower = searchTerm.toLowerCase()
    const matchesSearch = !searchTerm ||
      student.fullName.toLowerCase().includes(searchLower) ||
      student.email.toLowerCase().includes(searchLower) ||
      (student.departmentName || '').toLowerCase().includes(searchLower)

    const matchesStatus = statusFilter === 'all' ||
      student.status.status.toLowerCase() === statusFilter.toLowerCase()

    const matchesDept = studentDeptFilter.length === 0 ||
      studentDeptFilter.includes((student.departmentName || '').trim())

    const matchesCourse = studentCourseFilter === 'all' ||
      (student.courseName || '').trim() === studentCourseFilter

    return matchesSearch && matchesStatus && matchesDept && matchesCourse
  })

  // Helper: compute readiness for an exam student (GREEN/RED/GRAY)
  // Only based on pass/fail result - study readiness shown in student modal
  const getReadiness = (student) => {
    const pf = (student.passFail || '').toUpperCase()
    if (pf === 'PASS') return 'GREEN'
    if (pf === 'FAIL') return 'RED'
    // No exam result = neutral gray (avoids red dots looking like FAIL)
    return 'GRAY'
  }

  // Parse examDateRaw which can be M/D/YYYY (sheet) or YYYY-MM-DD (override)
  const parseExamDateRaw = (raw) => {
    if (!raw) return 0
    // YYYY-MM-DD format (from date overrides)
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return new Date(raw + 'T00:00:00').getTime()
    }
    // M/D/YYYY format (from Google Sheet CSV)
    const parts = raw.split('/')
    if (parts.length === 3) {
      return new Date(parts[2], parts[0] - 1, parts[1]).getTime()
    }
    // Fallback
    const d = new Date(raw)
    return isNaN(d.getTime()) ? 0 : d.getTime()
  }

  // Helper: compute days until exam and check filter match
  const matchesDaysFilter = (student, filter) => {
    if (filter === 'all') return true
    const examDateTs = parseExamDateRaw(student.examDateRaw)
    if (!examDateTs) return filter === 'no-date'

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const todayTs = today.getTime()
    const daysUntil = Math.ceil((examDateTs - todayTs) / 86400000)

    switch (filter) {
      case 'overdue': return daysUntil < 0
      case 'today': return daysUntil === 0
      case 'tomorrow': return daysUntil === 1
      case '2-days': return daysUntil >= 0 && daysUntil <= 2
      case '3-days': return daysUntil >= 0 && daysUntil <= 3
      case 'this-week': return daysUntil >= 0 && daysUntil <= 7
      case 'next-week': return daysUntil > 7 && daysUntil <= 14
      case 'this-month': return daysUntil >= 0 && daysUntil <= 30
      case 'no-date': return false
      default: return true
    }
  }

  const filteredExamStudents = examStudents.filter(student => {
    // Hidden student filter
    const isHidden = hiddenStudents.includes((student.email || '').toLowerCase().trim())
    if (showHidden) return isHidden
    if (isHidden) return false

    // In normal mode, only show students from logged-in department (matched in Absorb)
    if (!adminMode && student.matched === false) return false

    const searchLower = searchTerm.toLowerCase()
    const matchesSearch = !searchTerm ||
      (student.fullName || '').toLowerCase().includes(searchLower) ||
      (student.email || '').toLowerCase().includes(searchLower) ||
      (student.departmentName || '').toLowerCase().includes(searchLower) ||
      (student.agencyOwner || '').toLowerCase().includes(searchLower)

    // Exam result filter
    let matchesResult = true
    if (examResultFilter !== 'all') {
      const pf = (student.passFail || '').toUpperCase()
      const examDateTs = parseExamDateRaw(student.examDateRaw)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const isPast = examDateTs && examDateTs < today.getTime()
      const hasResult = pf === 'PASS' || pf === 'FAIL'

      switch (examResultFilter) {
        case 'passed': matchesResult = pf === 'PASS'; break
        case 'failed': matchesResult = pf === 'FAIL'; break
        case 'no-result': matchesResult = !hasResult; break
        case 'upcoming': matchesResult = !isPast && !hasResult; break
        case 'pending': matchesResult = isPast && !hasResult; break
        case 'at-risk':
          matchesResult = !isPast && !hasResult && student.matched !== false && (student.progress?.value || 0) < 80
          break
        default: matchesResult = true
      }
    }

    // Course type filter
    const matchesCourse = examCourseFilter === 'all' ||
      (student.examCourse || '').toLowerCase() === examCourseFilter.toLowerCase()

    // Department filter
    const matchesDept = examDeptFilter.length === 0 ||
      examDeptFilter.includes((student.departmentName || '').trim())

    // State filter
    const matchesState = examStateFilter === 'all' ||
      (student.examState || '').toLowerCase() === examStateFilter.toLowerCase()

    // Readiness filter (uses backend study readiness, not pass/fail dot color)
    const matchesReadiness = examReadinessFilter === 'all' ||
      (student.readiness?.status || 'GRAY') === examReadinessFilter

    // Days until exam filter
    const matchesDays = matchesDaysFilter(student, examDaysFilter)

    return matchesSearch && matchesResult && matchesCourse && matchesDept && matchesState && matchesReadiness && matchesDays
  })

  // Get unique values for filter dropdowns
  const hasOwnDataSource = ghlSettings?.enabled || bitrixSettings?.enabled || sheetSettings?.enabled
  const visibleExamStudents = (adminMode || hasOwnDataSource) ? examStudents : examStudents.filter(s => s.matched !== false)
  const examCourseTypes = [...new Set(visibleExamStudents.map(s => (s.examCourse || '').trim()).filter(Boolean))].sort()
  const examDepartments = [...new Set(visibleExamStudents.map(s => (s.departmentName || '').trim()).filter(Boolean))].sort()
  const examStates = [...new Set(visibleExamStudents.map(s => (s.examState || '').trim()).filter(Boolean))].sort()

  const formatLastSynced = () => {
    if (!lastSynced) return 'Never'

    const now = new Date()
    const diff = Math.floor((now - lastSynced) / 1000)

    if (diff < 60) return 'Just now'
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`
    return lastSynced.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Skeleton Header */}
        <header className="header shadow-lg">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-10 w-32 bg-white/20 rounded animate-pulse"></div>
                <div>
                  <div className="h-5 w-40 bg-white/20 rounded animate-pulse mb-1"></div>
                  <div className="h-4 w-24 bg-white/10 rounded animate-pulse"></div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="h-9 w-20 bg-white/10 rounded-lg animate-pulse"></div>
                <div className="h-9 w-9 bg-white/10 rounded-lg animate-pulse"></div>
              </div>
            </div>
          </div>
        </header>

        {/* Skeleton Content */}
        <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Loading indicator */}
          <div className="text-center mb-6">
            <div className="spinner mx-auto mb-2"></div>
            <p className="text-gray-600 text-sm">Loading student data...</p>
          </div>

          {/* Skeleton KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-md p-6 animate-pulse">
                <div className="h-4 w-24 bg-gray-200 rounded mb-3"></div>
                <div className="h-8 w-16 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>

          {/* Skeleton Table */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-4 border-b">
              <div className="h-10 w-64 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="divide-y">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-4 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                      <div>
                        <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                        <div className="h-3 w-48 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                    <div className="h-6 w-20 bg-gray-200 rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    )
  }

  // Resolved primary department name. The `department` prop comes from the
  // login response; if login's get_department call 401'd on Absorb, the
  // session stores the literal 'Department' placeholder and that's what
  // shows in the header. Once a multi-dept fetch completes, departmentMeta
  // carries the real name resolved from user records — prefer that when
  // available. Plain prop fallback otherwise.
  const primaryDeptName = (
    departmentMeta.find(
      d => d.id === department?.id
        && d.status === 'ok'
        && d.name
        && d.name !== 'Department'
        && d.name !== 'Unknown'
    )?.name
  ) || department?.name

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="header shadow-lg">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="/logo.png"
                alt="JustInsurance"
                className="h-20 w-auto"
                onError={(e) => { e.target.style.display = 'none' }}
              />
              <div>
                <h1 className="text-xl font-bold text-white">Licensed to Launch Dashboard</h1>
                <p className="text-sm text-ji-blue-light">{primaryDeptName}</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Sync Button */}
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
              >
                <svg
                  className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{syncing ? 'Syncing...' : 'Sync'}</span>
              </button>

              {/* User Menu */}
              <div className="flex items-center space-x-3">
                <div className="text-right hidden sm:block">
                  <p className="text-white font-medium">{user?.name}</p>
                  <p className="text-xs text-ji-blue-light">{user?.email}</p>
                </div>
                <button
                  onClick={onLogout}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                  title="Logout"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700">{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-white rounded-lg shadow-md p-1 max-w-xs">
          <button
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'students'
                ? 'bg-ji-blue-bright text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('students')}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Students</span>
            </div>
          </button>
          <button
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'exam'
                ? 'bg-ji-blue-bright text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
            onClick={() => setActiveTab('exam')}
          >
            <div className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Exam</span>
            </div>
          </button>
        </div>

        {/* Students Tab Content */}
        {activeTab === 'students' && (
          <>
            {/* Last Synced Info */}
            <div className="mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500">
                  Last updated: {formatLastSynced()}
                </p>
                {loadingFull && (
                  <span className="text-xs text-ji-blue-bright flex items-center gap-1">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Loading progress data...
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowCharts(!showCharts)}
                className="text-sm text-ji-blue-bright hover:text-ji-blue-medium flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>{showCharts ? 'Hide Charts' : 'Show Charts'}</span>
              </button>
            </div>

            {/* Department Manager */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <svg className="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    Departments ({1 + extraDepartments.length})
                  </span>
                  <span className="px-2 py-0.5 bg-ji-blue-bright/10 text-ji-blue-bright text-xs rounded-full font-medium">
                    {primaryDeptName || 'Primary'}
                  </span>
                  {(() => {
                    // Collapse the chip wall past a threshold: show the first
                    // row's worth plus a "+N more" expander. All chips keep
                    // their remove buttons when expanded.
                    const CHIP_LIMIT = 10
                    const resolved = departmentMeta.filter(d => d.status === 'ok' && d.id !== department?.id)
                    const unresolved = extraDepartments.filter(id => !departmentMeta.some(d => d.id === id && d.status === 'ok'))
                    const total = resolved.length + unresolved.length
                    const limit = deptChipsExpanded ? total : CHIP_LIMIT
                    const shownResolved = resolved.slice(0, limit)
                    const shownUnresolved = unresolved.slice(0, Math.max(0, limit - shownResolved.length))
                    const hidden = total - shownResolved.length - shownUnresolved.length
                    return (
                      <>
                        {shownResolved.map(d => (
                          <span
                            key={d.id}
                            className={`px-2 py-0.5 ${d.partial ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'} text-xs rounded-full flex items-center gap-1`}
                            title={d.partial ? `Partial load — ~${d.missing || '?'} students missing; Sync retries it` : undefined}
                          >
                            {d.name} ({d.studentCount}{d.partial ? '…' : ''})
                            <button
                              onClick={() => handleRemoveDepartment(d.id)}
                              className="text-red-400 hover:text-red-600 ml-0.5"
                              title="Remove department"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                        {shownUnresolved.map(id => (
                          <span key={id} className="px-2 py-0.5 bg-yellow-50 text-yellow-700 text-xs rounded-full flex items-center gap-1">
                            {id.substring(0, 8)}...
                            <button
                              onClick={() => handleRemoveDepartment(id)}
                              className="text-red-400 hover:text-red-600 ml-0.5"
                              title="Remove department"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        ))}
                        {hidden > 0 && (
                          <button
                            onClick={() => setDeptChipsExpanded(true)}
                            className="px-2 py-0.5 bg-ji-blue-bright/10 text-ji-blue-bright text-xs rounded-full font-medium hover:bg-ji-blue-bright/20"
                            title="Show all departments"
                          >
                            +{hidden} more
                          </button>
                        )}
                        {deptChipsExpanded && total > CHIP_LIMIT && (
                          <button
                            onClick={() => setDeptChipsExpanded(false)}
                            className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full font-medium hover:bg-gray-200"
                            title="Collapse the department list"
                          >
                            Show less
                          </button>
                        )}
                      </>
                    )
                  })()}
                </div>
                <div className="flex items-center gap-3">
                  {multiProgress && (
                    <span className="text-xs text-gray-500 flex items-center gap-1.5">
                      <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Loading departments {multiProgress.loaded}/{multiProgress.total}…
                    </span>
                  )}
                  {extraDepartments.length > 0 && (
                    <button
                      onClick={handleClearDepartments}
                      className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
                      title="Remove all added departments (your primary department stays)"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Clear All ({extraDepartments.length})</span>
                    </button>
                  )}
                  <button
                    onClick={handleLoadDeptTree}
                    disabled={treeLoading}
                    className="text-sm text-ji-blue-bright hover:text-ji-blue-medium flex items-center gap-1 disabled:opacity-50"
                    title="Fetch every sub-department under your primary department and load them all automatically"
                  >
                    {treeLoading ? (
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                    )}
                    <span>{treeLoading ? 'Loading Tree...' : 'Load Dept Tree'}</span>
                  </button>
                  <button
                    onClick={() => { setShowDeptManager(!showDeptManager); setDeptError('') }}
                    className="text-sm text-ji-blue-bright hover:text-ji-blue-medium flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={showDeptManager ? "M5 15l7-7 7 7" : "M12 4v16m8-8H4"} />
                    </svg>
                    <span>{showDeptManager ? 'Close' : 'Add Department'}</span>
                  </button>
                </div>
              </div>
              {deptError && !showDeptManager && (
                <p className="text-xs text-gray-600 mt-2">{deptError}</p>
              )}

              {showDeptManager && (
                <div className="mt-3 flex gap-2 items-start">
                  <div className="flex-1">
                    <textarea
                      rows={2}
                      placeholder="Paste one or more Department IDs (one per line, or separated by commas). Press Enter to add, Shift+Enter for a new line."
                      value={deptInputValue}
                      onChange={(e) => { setDeptInputValue(e.target.value); setDeptError('') }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleAddDepartment()
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ji-blue-bright font-mono resize-y"
                    />
                    {deptError && <p className="text-red-500 text-xs mt-1">{deptError}</p>}
                  </div>
                  <button
                    onClick={handleAddDepartment}
                    className="btn btn-primary text-sm py-2 px-4 whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>

            {/* KPI Cards */}
            {summary && <KPICards summary={summary} />}

            {/* Charts (collapsible) */}
            {showCharts && summary && (
              <div className="mb-8 animate-fadeIn">
                <Charts summary={summary} students={students} />
              </div>
            )}

            {/* Filters and Search */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Search by name, email, or department..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                  {/* Department Filter - multi-select checklist */}
                  {studentDepartments.length > 0 && (
                    <div className="relative">
                      <button
                        onClick={() => setShowDeptDropdown(!showDeptDropdown)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright bg-white flex items-center gap-2 text-sm"
                      >
                        <span>
                          {studentDeptFilter.length === 0
                            ? 'All Departments'
                            : `${studentDeptFilter.length} dept${studentDeptFilter.length > 1 ? 's' : ''}`}
                        </span>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={showDeptDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                        </svg>
                      </button>
                      {showDeptDropdown && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setShowDeptDropdown(false)} />
                          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] py-1">
                            <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100">
                              <input
                                type="checkbox"
                                checked={studentDeptFilter.length === 0}
                                onChange={() => setStudentDeptFilter([])}
                                className="rounded text-ji-blue-bright"
                              />
                              <span className="font-medium">All Departments</span>
                            </label>
                            {studentDepartments.map(d => (
                              <label key={d} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                                <input
                                  type="checkbox"
                                  checked={studentDeptFilter.includes(d)}
                                  onChange={() => {
                                    setStudentDeptFilter(prev =>
                                      prev.includes(d)
                                        ? prev.filter(x => x !== d)
                                        : [...prev, d]
                                    )
                                  }}
                                  className="rounded text-ji-blue-bright"
                                />
                                <span className="truncate">{d}</span>
                              </label>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Status Filter */}
                  <select
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="complete">Complete</option>
                    <option value="active">Active</option>
                    <option value="warning">Warning</option>
                    <option value="re-engage">Re-engage</option>
                    <option value="abandoned">Abandoned</option>
                    <option value="course expired">Course Expired</option>
                  </select>

                  {/* Course Filter */}
                  {studentCourseTypes.length > 1 && (
                    <select
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright"
                      value={studentCourseFilter}
                      onChange={(e) => setStudentCourseFilter(e.target.value)}
                    >
                      <option value="all">All Courses</option>
                      {studentCourseTypes.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  )}

                  {/* Clear All Filters */}
                  {(searchTerm || statusFilter !== 'all' || studentCourseFilter !== 'all' || studentDeptFilter.length > 0) && (
                    <button
                      onClick={() => {
                        setSearchTerm('')
                        setStatusFilter('all')
                        setStudentCourseFilter('all')
                        setStudentDeptFilter([])
                      }}
                      className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors"
                    >
                      Clear All
                    </button>
                  )}

                  {/* Export Button */}
                  <button
                    onClick={handleExport}
                    className="btn btn-secondary flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Export CSV</span>
                  </button>

                  {/* Show Hidden Toggle */}
                  {hiddenStudents.length > 0 && (
                    <button
                      onClick={() => setShowHidden(!showHidden)}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
                        showHidden
                          ? 'bg-amber-50 border-amber-300 text-amber-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                      <span>Hidden ({hiddenStudents.length})</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Results Count */}
              <div className="mt-3 text-sm text-gray-500">
                {showHidden
                  ? `Showing ${filteredStudents.length} hidden students`
                  : `Showing ${filteredStudents.length} of ${students.length} students`}
                {!showHidden && hiddenStudents.length > 0 && ` (${hiddenStudents.length} hidden)`}
                {extraDepartments.length > 0 && ` across ${1 + extraDepartments.length} departments`}
              </div>
            </div>

            {/* Student Table. While a batched multi-dept load is still
                streaming (multiProgress active) and no rows have arrived yet,
                show a progress panel instead of StudentTable's "No students
                found" — on big trees the first batches are often tiny (0-1
                student) depts, so an honest-but-alarming empty table sat on
                screen for a minute while the large depts were still loading. */}
            {multiProgress && filteredStudents.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <svg className="animate-spin w-10 h-10 mx-auto text-ji-blue-bright mb-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <h3 className="text-lg font-medium text-gray-600 mb-1">Loading students…</h3>
                <p className="text-gray-500 text-sm">
                  Departments {multiProgress.loaded}/{multiProgress.total} loaded so far —
                  large loads can take a minute or two the first time.
                </p>
              </div>
            ) : (
              <StudentTable
                students={filteredStudents}
                onViewStudent={setSelectedStudent}
                showDepartment={extraDepartments.length > 0}
                onHideStudent={showHidden ? handleUnhideStudent : handleHideStudent}
                showHidden={showHidden}
              />
            )}
          </>
        )}

        {/* Exam Tab Content */}
        {activeTab === 'exam' && (
          <>
            {/* Exam Tab Header */}
            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <p className="text-sm text-gray-500">
                  Exam scheduling, pass rates & study tracking
                </p>
                <button
                  onClick={() => fetchExamData()}
                  disabled={examLoading}
                  className="text-sm text-ji-blue-bright hover:text-ji-blue-medium flex items-center space-x-1"
                >
                  <svg className={`w-4 h-4 ${examLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>{examLoading ? 'Refreshing...' : 'Refresh'}</span>
                </button>
                {ghlSettings?.enabled && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700 border border-teal-200">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    GHL Mode
                  </span>
                )}
                {bitrixSettings?.enabled && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Bitrix Mode
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {/* Admin Mode Toggle */}
                {adminMode ? (
                  <div className="flex items-center gap-2">
                    {/* Scheduler status indicator */}
                    {schedulerInfo && (
                      <span className={`text-xs px-2 py-1 rounded-lg border ${
                        schedulerInfo.enabled
                          ? 'text-green-600 border-green-200 bg-green-50'
                          : 'text-gray-500 border-gray-200 bg-gray-50'
                      }`} title={schedulerInfo.lastSync ? `Last: ${new Date(schedulerInfo.lastSync).toLocaleString()}` : 'No sync yet'}>
                        {schedulerInfo.enabled
                          ? `Auto-sync: every ${schedulerInfo.intervalHours}h${schedulerInfo.lastSync ? ` | Last: ${(() => {
                              const mins = Math.round((Date.now() - new Date(schedulerInfo.lastSync).getTime()) / 60000)
                              if (mins < 1) return 'just now'
                              if (mins < 60) return `${mins}m ago`
                              const hrs = Math.round(mins / 60)
                              return `${hrs}h ago`
                            })()}` : ''}`
                          : 'Auto-sync: not configured'
                        }
                      </span>
                    )}

                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Admin Mode
                    </span>
                    <button
                      onClick={handleAdminLogout}
                      className="text-xs text-gray-500 hover:text-red-500"
                    >
                      Exit
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setShowAdminInput(!showAdminInput)}
                      className="text-sm text-purple-500 hover:text-purple-700 flex items-center space-x-1 px-2.5 py-1 rounded-lg border border-purple-200 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                      title="Admin Override"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <span>Admin</span>
                    </button>
                    {showAdminInput && (
                      <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border p-3 z-50 w-64">
                        <p className="text-xs text-gray-500 mb-2">Enter admin password to view all student data</p>
                        <form onSubmit={(e) => { e.preventDefault(); handleAdminLogin(e.target.password.value) }}>
                          <input
                            name="password"
                            type="password"
                            placeholder="Admin password"
                            className="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            autoFocus
                          />
                          {adminError && <p className="text-xs text-red-500 mt-1">{adminError}</p>}
                          <div className="flex gap-2 mt-2">
                            <button type="submit" className="flex-1 px-3 py-1.5 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700">
                              Unlock
                            </button>
                            <button type="button" onClick={() => { setShowAdminInput(false); setAdminError('') }} className="px-3 py-1.5 bg-gray-100 rounded text-xs text-gray-600 hover:bg-gray-200">
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { setShowGhlSetup(!showGhlSetup); setShowBitrixSetup(false); setShowSheetSetup(false) }}
                  className={`text-sm flex items-center space-x-1 px-2.5 py-1 rounded-lg border transition-colors ${
                    ghlSettings?.enabled
                      ? 'text-teal-600 border-teal-200 hover:border-teal-400 hover:bg-teal-50'
                      : bitrixSettings?.enabled
                        ? 'text-blue-600 border-blue-200 hover:border-blue-400 hover:bg-blue-50'
                        : sheetSettings?.enabled
                          ? 'text-green-600 border-green-200 hover:border-green-400 hover:bg-green-50'
                          : 'text-gray-500 border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                  title="Configure exam data source"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                  <span>Data Source</span>
                </button>
                <button
                  onClick={() => setShowExamCharts(!showExamCharts)}
                  className="text-sm text-ji-blue-bright hover:text-ji-blue-medium flex items-center space-x-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>{showExamCharts ? 'Hide Charts' : 'Show Charts'}</span>
                </button>
              </div>
            </div>

            {/* GHL Data Source Setup Panel */}
            {showGhlSetup && (
              <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-l-4 border-teal-500">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-700">Exam Data Source</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      ghlSettings?.enabled
                        ? 'bg-teal-100 text-teal-700'
                        : bitrixSettings?.enabled
                          ? 'bg-blue-100 text-blue-700'
                          : sheetSettings?.enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                    }`}>
                      {ghlSettings?.enabled ? 'GHL Calendar' : bitrixSettings?.enabled ? 'Bitrix24 CRM' : sheetSettings?.enabled ? 'Your Google Sheet' : 'Admin Sheet'}
                    </span>
                  </div>
                  <button onClick={() => setShowGhlSetup(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {ghlSettings?.enabled ? (
                  <div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <span>Token: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{ghlSettings.ghl_token || 'Not set'}</code></span>
                      <span>Location: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{ghlSettings.location_id || 'Not set'}</code></span>
                      <span>Calendar: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{ghlSettings.calendar_id || 'Not set'}</code></span>
                    </div>
                    <button
                      onClick={() => handleSaveGhlSettings(false)}
                      disabled={ghlSaving}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 transition-colors"
                    >
                      {ghlSaving ? 'Saving...' : 'Disable GHL (use Google Sheet)'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                      Connect a GoHighLevel calendar to pull exam appointments instead of the Google Sheet.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">GHL Private Integration Token</label>
                        <input
                          type="password"
                          value={ghlTokenInput}
                          onChange={e => setGhlTokenInput(e.target.value)}
                          placeholder="pit-xxxxxxxx..."
                          className="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Location ID</label>
                        <input
                          type="text"
                          value={ghlLocationInput}
                          onChange={e => setGhlLocationInput(e.target.value)}
                          placeholder="Location ID from GHL"
                          className="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    </div>
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Calendar</label>
                        {ghlCalendars.length > 0 ? (
                          <select
                            value={ghlCalendarInput}
                            onChange={e => setGhlCalendarInput(e.target.value)}
                            className="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                            <option value="">Select a calendar...</option>
                            {ghlCalendars.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        ) : (
                          <p className="text-xs text-gray-400 py-1.5">Enter token & location, then fetch calendars</p>
                        )}
                      </div>
                      <button
                        onClick={handleFetchGhlCalendars}
                        disabled={ghlFetchingCals || !ghlTokenInput.trim() || !ghlLocationInput.trim()}
                        className="px-3 py-1.5 bg-teal-50 text-teal-700 rounded text-xs font-medium hover:bg-teal-100 border border-teal-200 disabled:opacity-50 transition-colors whitespace-nowrap"
                      >
                        {ghlFetchingCals ? 'Fetching...' : 'Fetch Calendars'}
                      </button>
                    </div>
                    {ghlError && <p className="text-xs text-red-500">{ghlError}</p>}
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleSaveGhlSettings(true)}
                        disabled={ghlSaving || !ghlCalendarInput}
                        className="px-4 py-1.5 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
                      >
                        {ghlSaving ? 'Enabling...' : 'Enable GHL'}
                      </button>
                      <button
                        onClick={() => { setShowGhlSetup(false); setShowBitrixSetup(true) }}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded text-xs font-medium hover:bg-blue-100 border border-blue-200 transition-colors"
                      >
                        Use Bitrix24 instead
                      </button>
                      <button
                        onClick={() => { setShowGhlSetup(false); setShowSheetSetup(true) }}
                        className="px-3 py-1.5 bg-green-50 text-green-600 rounded text-xs font-medium hover:bg-green-100 border border-green-200 transition-colors"
                      >
                        Use Your Sheet instead
                      </button>
                      <button
                        onClick={() => setShowGhlSetup(false)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bitrix24 Data Source Setup Panel */}
            {showBitrixSetup && (
              <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-700">Bitrix24 CRM Integration</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      bitrixSettings?.enabled
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {bitrixSettings?.enabled ? 'Bitrix24 CRM' : 'Not Connected'}
                    </span>
                  </div>
                  <button onClick={() => setShowBitrixSetup(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {bitrixSettings?.enabled ? (
                  <div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <span>Webhook: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{bitrixSettings.webhook_url || 'Not set'}</code></span>
                    </div>
                    <button
                      onClick={() => handleSaveBitrixSettings(false)}
                      disabled={bitrixSaving}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 transition-colors"
                    >
                      {bitrixSaving ? 'Saving...' : 'Disable Bitrix24 (use Google Sheet)'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500">
                      Connect your Bitrix24 CRM to pull exam appointments from CRM activities. Create an inbound webhook in your Bitrix24 portal with <strong>CRM</strong> and <strong>Calendar</strong> scopes enabled.
                    </p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Bitrix24 Webhook URL</label>
                      <input
                        type="text"
                        value={bitrixWebhookInput}
                        onChange={e => { setBitrixWebhookInput(e.target.value); setBitrixValidated(false) }}
                        placeholder="https://yourcompany.bitrix24.com/rest/1/your_secret/"
                        className="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleValidateBitrix}
                        disabled={bitrixValidating || !bitrixWebhookInput.trim()}
                        className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded text-xs font-medium hover:bg-blue-100 border border-blue-200 disabled:opacity-50 transition-colors whitespace-nowrap"
                      >
                        {bitrixValidating ? 'Validating...' : 'Validate Webhook'}
                      </button>
                      {bitrixValidated && (
                        <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Connected
                        </span>
                      )}
                    </div>
                    {bitrixError && <p className="text-xs text-red-500">{bitrixError}</p>}
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => handleSaveBitrixSettings(true)}
                        disabled={bitrixSaving || !bitrixValidated}
                        className="px-4 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {bitrixSaving ? 'Enabling...' : 'Enable Bitrix24'}
                      </button>
                      <button
                        onClick={() => { setShowBitrixSetup(false); setShowGhlSetup(true) }}
                        className="px-3 py-1.5 bg-teal-50 text-teal-600 rounded text-xs font-medium hover:bg-teal-100 border border-teal-200 transition-colors"
                      >
                        Use GHL instead
                      </button>
                      <button
                        onClick={() => { setShowBitrixSetup(false); setShowSheetSetup(true) }}
                        className="px-3 py-1.5 bg-green-50 text-green-600 rounded text-xs font-medium hover:bg-green-100 border border-green-200 transition-colors"
                      >
                        Use Your Sheet instead
                      </button>
                      <button
                        onClick={() => setShowBitrixSetup(false)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Google Sheet Data Source Setup Panel */}
            {showSheetSetup && (
              <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-700">Your Google Sheet</h3>
                    {sheetSettings?.enabled && (
                      <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-green-100 text-green-700">Active</span>
                    )}
                  </div>
                  <button onClick={() => setShowSheetSetup(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {sheetSettings?.enabled ? (
                  <div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <span>Sheet: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{sheetSettings.sheet_url_masked || sheetSettings.sheet_url || 'Not set'}</code></span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveSheetSettings(false)}
                        className="px-3 py-1.5 bg-red-50 text-red-600 rounded text-xs font-medium hover:bg-red-100 border border-red-200 transition-colors"
                        disabled={sheetSaving}
                      >
                        {sheetSaving ? 'Disabling...' : 'Disable Sheet'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-gray-500 mb-3">
                      Connect your own Google Sheet to populate the Exam tab. Sheet must be shared as <strong>"Anyone with the link can view"</strong>.
                    </p>
                    <p className="text-xs text-gray-400 mb-2">
                      Use these as <strong>column headers</strong> in Row 1 (any order, case-insensitive):
                    </p>
                    <p className="text-xs text-gray-400 mb-1">
                      Required: <strong>Email</strong>, <strong>Exam Date</strong>
                    </p>
                    <p className="text-xs text-gray-400 mb-3">
                      Optional: <strong>Student Name</strong>, <strong>Phone</strong>, <strong>Exam Time</strong>, <strong>State</strong>, <strong>Course</strong>, <strong>Agency Owner</strong>, <strong>Pass/Fail</strong>, <strong>Final Outcome</strong>
                    </p>
                    <div className="space-y-2 mb-3">
                      <input
                        type="text"
                        placeholder="Paste Google Sheet URL or ID"
                        value={sheetUrlInput}
                        onChange={e => { setSheetUrlInput(e.target.value); setSheetValidated(false); setSheetError('') }}
                        className="w-full px-3 py-1.5 border rounded text-sm focus:ring-1 focus:ring-green-300 focus:border-green-400"
                      />
                    </div>
                    {sheetValidated && sheetValidationInfo && (
                      <div className="bg-green-50 border border-green-200 rounded p-2 mb-3">
                        <p className="text-xs text-green-700 font-medium">Sheet validated successfully</p>
                        <p className="text-xs text-green-600">{sheetValidationInfo.row_count} student{sheetValidationInfo.row_count !== 1 ? 's' : ''} found</p>
                      </div>
                    )}
                    {sheetError && (
                      <p className="text-xs text-red-500 mb-2">{sheetError}</p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={handleValidateSheet}
                        disabled={sheetValidating || !sheetUrlInput.trim()}
                        className="px-3 py-1.5 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200 disabled:opacity-50 transition-colors"
                      >
                        {sheetValidating ? 'Validating...' : 'Validate Sheet'}
                      </button>
                      <button
                        onClick={() => handleSaveSheetSettings(true)}
                        disabled={sheetSaving || !sheetValidated}
                        className="px-4 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        {sheetSaving ? 'Enabling...' : 'Enable Sheet'}
                      </button>
                      <button
                        onClick={() => { setShowSheetSetup(false); setShowGhlSetup(true) }}
                        className="px-3 py-1.5 bg-teal-50 text-teal-600 rounded text-xs font-medium hover:bg-teal-100 border border-teal-200 transition-colors"
                      >
                        Use GHL instead
                      </button>
                      <button
                        onClick={() => { setShowSheetSetup(false); setShowBitrixSetup(true) }}
                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded text-xs font-medium hover:bg-blue-100 border border-blue-200 transition-colors"
                      >
                        Use Bitrix24 instead
                      </button>
                      <button
                        onClick={() => setShowSheetSetup(false)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded text-xs hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Allowed Users Panel - Admin Only */}
            {adminMode && (
              <div className="bg-white rounded-xl shadow-md p-4 mb-6 border-l-4 border-purple-500">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-700">Allowed Users</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      allowlistEnforcing
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {allowlistEnforcing ? `Active (${allowedUsers.length} users)` : 'Not enforcing'}
                    </span>
                  </div>
                  <button
                    onClick={() => { setShowAllowlist(!showAllowlist); if (!showAllowlist) fetchAllowlist() }}
                    className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                  >
                    {showAllowlist ? 'Hide' : 'Manage'}
                  </button>
                </div>

                {!allowlistSheetLoaded && (
                  <p className="text-xs text-amber-600 font-medium mb-1">
                    ⚠ Google Sheet not loaded — this list may be incomplete, and if it shows empty,
                    access enforcement is OFF for everyone until the sheet loads.
                  </p>
                )}
                {!allowlistEnforcing && !showAllowlist && (
                  <p className="text-xs text-gray-500">
                    No users in allowlist. All authenticated Absorb users can log in. Add a user to start restricting access.
                  </p>
                )}

                {showAllowlist && (
                  <div className="mt-3">
                    <div className="flex gap-2 mb-3">
                      <input
                        type="email"
                        placeholder="Email address"
                        value={newAllowEmail}
                        onChange={(e) => { setNewAllowEmail(e.target.value); setAllowlistError('') }}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddAllowedUser()}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <input
                        type="text"
                        placeholder="Name (optional)"
                        value={newAllowName}
                        onChange={(e) => setNewAllowName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddAllowedUser()}
                        className="w-40 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <button
                        onClick={handleAddAllowedUser}
                        disabled={allowlistSaving}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
                      >
                        {allowlistSaving ? 'Saving...' : 'Add'}
                      </button>
                    </div>
                    {allowlistError && <p className="text-xs text-red-500 mb-2">{allowlistError}</p>}

                    {allowlistLoading ? (
                      <p className="text-sm text-gray-400">Loading...</p>
                    ) : allowlistLoadError ? (
                      <div className="text-sm text-red-500 flex items-center gap-3">
                        <span>{allowlistLoadError}</span>
                        <button
                          onClick={() => fetchAllowlist()}
                          className="text-xs px-2 py-1 border border-red-300 rounded hover:bg-red-50"
                        >
                          Retry
                        </button>
                      </div>
                    ) : allowedUsers.length > 0 ? (
                      <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                        {allowedUsers.map(u => (
                          <div key={u.email} className="flex items-center justify-between py-2">
                            <div>
                              <p className="text-sm font-medium text-gray-800">{u.name || u.email}</p>
                              <p className="text-xs text-gray-500">{u.email} &middot; Added {new Date(u.added_at).toLocaleDateString()}</p>
                            </div>
                            <button
                              onClick={() => handleRemoveAllowedUser(u.email)}
                              disabled={allowlistSaving}
                              className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-40"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">No users in allowlist yet.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Exam Charts (collapsible) */}
            {showExamCharts && examSummary && (
              <div className="mb-8 animate-fadeIn">
                <ExamCharts examSummary={examSummary} students={examStudents} />
              </div>
            )}

            {/* Exam KPI Row 1 - Core Metrics */}
            {examSummary && (
              <>
                {kpisFiltered && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                      KPIs filtered ({examSummary.total} students)
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-4">
                  <div className="kpi-card bg-gray-50 border-l-4 border-gray-500 animate-fadeIn">
                    <p className="text-sm font-medium text-gray-600">Total Students</p>
                    <p className="kpi-value text-gray-800">{examSummary.total}</p>
                    <p className="text-xs text-gray-500 mt-1">Exam scheduled</p>
                  </div>
                  <div className="kpi-card bg-blue-50 border-l-4 border-blue-500 animate-fadeIn" style={{animationDelay:'50ms'}}>
                    <p className="text-sm font-medium text-gray-600">Upcoming</p>
                    <p className="kpi-value text-blue-800">{examSummary.upcoming}</p>
                    <p className="text-xs text-gray-500 mt-1">Exams scheduled</p>
                  </div>
                  <div className="kpi-card bg-green-50 border-l-4 border-green-500 animate-fadeIn" style={{animationDelay:'100ms'}}>
                    <p className="text-sm font-medium text-gray-600">Pass Rate</p>
                    <p className="kpi-value text-green-800">{examSummary.passRate}%</p>
                    <p className="text-xs text-gray-500 mt-1">{examSummary.passed} passed / {examSummary.passed + examSummary.failed} taken</p>
                  </div>
                  <div className="kpi-card bg-red-50 border-l-4 border-red-500 animate-fadeIn" style={{animationDelay:'150ms'}}>
                    <p className="text-sm font-medium text-gray-600">Failed</p>
                    <p className="kpi-value text-red-800">{examSummary.failed}</p>
                    <p className="text-xs text-gray-500 mt-1">Exam result</p>
                  </div>
                  <div className="kpi-card bg-amber-50 border-l-4 border-amber-500 animate-fadeIn" style={{animationDelay:'200ms'}}>
                    <p className="text-sm font-medium text-gray-600">At Risk</p>
                    <p className="kpi-value text-amber-800">{examSummary.atRisk}</p>
                    <p className="text-xs text-gray-500 mt-1">Upcoming + &lt;80% progress</p>
                  </div>
                </div>

                {/* KPI Row 2 - Study Time Analytics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="kpi-card bg-purple-50 border-l-4 border-purple-500 animate-fadeIn" style={{animationDelay:'250ms'}}>
                    <p className="text-sm font-medium text-gray-600">Avg Study Time</p>
                    <p className="kpi-value text-purple-800">{examSummary.avgStudyTimeFormatted}</p>
                    <p className="text-xs text-gray-500 mt-1">All tracked students</p>
                  </div>
                  <div className="kpi-card bg-green-50 border-l-4 border-green-400 animate-fadeIn" style={{animationDelay:'300ms'}}>
                    <p className="text-sm font-medium text-gray-600">Avg Time (Passed)</p>
                    <p className="kpi-value text-green-700">{examSummary.avgStudyPassedFormatted}</p>
                    <p className="text-xs text-gray-500 mt-1">Students who passed</p>
                  </div>
                  <div className="kpi-card bg-red-50 border-l-4 border-red-400 animate-fadeIn" style={{animationDelay:'350ms'}}>
                    <p className="text-sm font-medium text-gray-600">Avg Time (Failed)</p>
                    <p className="kpi-value text-red-700">{examSummary.avgStudyFailedFormatted}</p>
                    <p className="text-xs text-gray-500 mt-1">Students who failed</p>
                  </div>
                  <div className="kpi-card bg-indigo-50 border-l-4 border-indigo-500 animate-fadeIn" style={{animationDelay:'400ms'}}>
                    <p className="text-sm font-medium text-gray-600">Avg Progress</p>
                    <p className="kpi-value text-indigo-800">{examSummary.averageProgress}%</p>
                    <p className="text-xs text-gray-500 mt-1">Course completion</p>
                  </div>
                </div>

                {/* Course Type Pass Rates */}
                {examSummary.courseTypes && Object.keys(examSummary.courseTypes).length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Pass Rate by Course Type</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {Object.entries(examSummary.courseTypes).map(([course, data]) => {
                        const p = data.passed || 0
                        const f = data.failed || 0
                        const taken = p + f
                        const rate = taken > 0 ? Math.round(p / taken * 100) : null
                        return (
                          <div key={course} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{course}</p>
                              <p className="text-xs text-gray-500">{data.total} students</p>
                            </div>
                            <div className="text-right">
                              {rate !== null ? (
                                <>
                                  <p className={`text-lg font-bold ${rate >= 70 ? 'text-green-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                    {rate}%
                                  </p>
                                  <p className="text-xs text-gray-500">{p}P / {f}F</p>
                                </>
                              ) : (
                                <p className="text-sm text-gray-400">No results</p>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Search/Filter for Exam Tab */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
              {/* Row 1: Search + Result filter */}
              <div className="flex flex-col sm:flex-row gap-3 items-center">
                <div className="relative flex-1 max-w-md">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright text-sm"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                {/* Exam Result Filter */}
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright text-sm"
                  value={examResultFilter}
                  onChange={(e) => setExamResultFilter(e.target.value)}
                >
                  <option value="no-result">No Result</option>
                  <option value="all">All Results</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="passed">Passed</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                  <option value="at-risk">At Risk</option>
                </select>
              </div>

              {/* Row 2: All other filters */}
              <div className="flex items-center gap-3 flex-wrap mt-3">
                {/* Department Filter - multi-select checklist */}
                <div className="relative">
                  <button
                    onClick={() => setShowExamDeptDropdown(!showExamDeptDropdown)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright bg-white flex items-center gap-2 text-sm"
                  >
                    <span>
                      {examDeptFilter.length === 0
                        ? 'All Departments'
                        : `${examDeptFilter.length} dept${examDeptFilter.length > 1 ? 's' : ''}`}
                    </span>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={showExamDeptDropdown ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                    </svg>
                  </button>
                  {showExamDeptDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowExamDeptDropdown(false)} />
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[200px] py-1">
                        <label className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100">
                          <input
                            type="checkbox"
                            checked={examDeptFilter.length === 0}
                            onChange={() => setExamDeptFilter([])}
                            className="rounded text-ji-blue-bright"
                          />
                          <span className="font-medium">All Departments</span>
                        </label>
                        {examDepartments.map(d => (
                          <label key={d} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={examDeptFilter.includes(d)}
                              onChange={() => {
                                setExamDeptFilter(prev =>
                                  prev.includes(d)
                                    ? prev.filter(x => x !== d)
                                    : [...prev, d]
                                )
                              }}
                              className="rounded text-ji-blue-bright"
                            />
                            <span className="truncate">{d}</span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Readiness Filter */}
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright text-sm"
                  value={examReadinessFilter}
                  onChange={(e) => setExamReadinessFilter(e.target.value)}
                >
                  <option value="all">All Readiness</option>
                  <option value="GREEN">Green - On Track</option>
                  <option value="YELLOW">Yellow - Needs Attention</option>
                  <option value="RED">Red - At Risk</option>
                </select>

                {/* Days Until Exam Filter */}
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright text-sm"
                  value={examDaysFilter}
                  onChange={(e) => setExamDaysFilter(e.target.value)}
                >
                  <option value="all">All Dates</option>
                  <option value="overdue">Overdue</option>
                  <option value="today">Today</option>
                  <option value="tomorrow">Tomorrow</option>
                  <option value="2-days">Within 2 Days</option>
                  <option value="3-days">Within 3 Days</option>
                  <option value="this-week">This Week</option>
                  <option value="next-week">Next 2 Weeks</option>
                  <option value="this-month">This Month</option>
                </select>

                {/* State Filter */}
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright text-sm"
                  value={examStateFilter}
                  onChange={(e) => setExamStateFilter(e.target.value)}
                >
                  <option value="all">All States</option>
                  {examStates.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>

                {/* Course Type Filter */}
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ji-blue-bright text-sm"
                  value={examCourseFilter}
                  onChange={(e) => setExamCourseFilter(e.target.value)}
                >
                  <option value="all">All Courses</option>
                  {examCourseTypes.map(ct => (
                    <option key={ct} value={ct}>{ct}</option>
                  ))}
                </select>

                {/* Clear Filters Button */}
                {(examDeptFilter.length > 0 || examReadinessFilter !== 'all' || examDaysFilter !== 'all' || examStateFilter !== 'all' || examCourseFilter !== 'all' || examResultFilter !== 'all' || searchTerm) && (
                  <button
                    onClick={() => {
                      setExamDeptFilter([])
                      setExamReadinessFilter('all')
                      setExamDaysFilter('all')
                      setExamStateFilter('all')
                      setExamCourseFilter('all')
                      setExamResultFilter('all')
                      setSearchTerm('')
                    }}
                    className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                  >
                    Clear All
                  </button>
                )}

                {/* Show Hidden Toggle */}
                {hiddenStudents.length > 0 && (
                  <button
                    onClick={() => setShowHidden(!showHidden)}
                    className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
                      showHidden
                        ? 'bg-amber-50 border-amber-300 text-amber-700'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                    <span>Hidden ({hiddenStudents.length})</span>
                  </button>
                )}
              </div>

              <div className="mt-3 flex items-center gap-3 text-sm text-gray-500">
                <span>
                  {showHidden
                    ? `Showing ${filteredExamStudents.length} hidden exam students`
                    : `Showing ${filteredExamStudents.length} of ${visibleExamStudents.length} exam students`}
                  {!showHidden && hiddenStudents.length > 0 && ` (${hiddenStudents.length} hidden)`}
                </span>
                {filteredExamStudents.length !== examStudents.length && !kpisFiltered && (
                  <button
                    onClick={() => { setExamSummary(calculateFullKPIs(filteredExamStudents)); setKpisFiltered(true) }}
                    className="px-3 py-1 bg-ji-blue-bright text-white rounded-lg text-xs font-medium hover:bg-ji-blue-medium transition-colors"
                  >
                    Calculate KPIs by Filter
                  </button>
                )}
                {kpisFiltered && (
                  <button
                    onClick={() => { setExamSummary(calculateFullKPIs(examStudents)); setKpisFiltered(false) }}
                    className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-300 transition-colors"
                  >
                    Reset KPIs to All
                  </button>
                )}
              </div>
            </div>

            {/* Exam Loading State */}
            {examLoading && !examLoaded ? (
              <div className="bg-white rounded-xl shadow-md p-8 text-center">
                <div className="spinner mx-auto mb-4"></div>
                <p className="text-gray-500">Loading exam schedule...</p>
              </div>
            ) : (
              <ExamTable
                students={filteredExamStudents}
                onViewStudent={setSelectedStudent}
                adminMode={adminMode}
                onHideStudent={showHidden ? handleUnhideStudent : handleHideStudent}
                showHidden={showHidden}
              />
            )}
          </>
        )}
      </main>

      {/* Student Detail Modal - Absorb data available */}
      {selectedStudent && selectedStudent.id && (
        <StudentModal
          studentId={selectedStudent.id}
          examInfo={selectedStudent.examDate ? {
            examDate: selectedStudent.examDate,
            examDateRaw: selectedStudent.examDateRaw,
            examTime: selectedStudent.examTime,
            examState: selectedStudent.examState,
            examCourse: selectedStudent.examCourse,
            agencyOwner: selectedStudent.agencyOwner,
            passFail: selectedStudent.passFail,
            finalOutcome: selectedStudent.finalOutcome,
            departmentName: selectedStudent.departmentName,
            sheetTracking: selectedStudent.sheetTracking,
            email: selectedStudent.email
          } : null}
          onClose={() => setSelectedStudent(null)}
          onSessionExpired={onLogout}
          onUpdateResult={activeTab === 'exam' ? handleUpdateResult : null}
          onUpdateExamDate={activeTab === 'exam' ? handleUpdateExamDate : null}
          onUpdateStudentContact={handleUpdateStudentContact}
        />
      )}

      {/* Exam Sheet Modal - No Absorb data, show sheet info */}
      {selectedStudent && !selectedStudent.id && (
        <ExamSheetModal
          student={selectedStudent}
          adminMode={adminMode}
          onClose={() => setSelectedStudent(null)}
          onUpdateResult={handleUpdateResult}
          onUpdateExamDate={handleUpdateExamDate}
          onUpdateContact={handleUpdateSheetContact}
        />
      )}
    </div>
  )
}

export default Dashboard
